/**
 * Auto-fix engine for common backup issues before restore.
 * Applies safe, deterministic repairs:
 *  - Convert non-ISO date strings to ISO (e.g. "2025/01/05" -> "2025-01-05T00:00:00.000Z")
 *  - Lowercase + trim UUIDs and reject malformed ones (drop field if not fixable)
 *  - Convert empty strings ("") in nullable fields to null
 *  - Coerce numeric strings to numbers when the schema expects a number
 *  - Coerce boolean-like strings ("true"/"false"/0/1) to booleans
 *  - Trim whitespace from string fields
 *  - Drop duplicate rows by `id` (keeps the first occurrence)
 *  - Generate UUIDs for rows missing `id` (when table has `id` column convention)
 *  - Force `nursery_id` to expected nursery if explicitly empty/null
 */

export interface AutoFixIssue {
  table: string;
  rowIndex: number;
  field?: string;
  before: any;
  after: any;
  reason: string;
}

export interface AutoFixReport {
  totalChanges: number;
  fixedRows: number;
  droppedRows: number;
  byTable: Record<string, { changes: number; dropped: number }>;
  issues: AutoFixIssue[]; // capped sample for UI
  fullCount: number;      // total issues recorded (may exceed sample size)
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SAMPLE_LIMIT = 200;

// Schema hints — same as validator. Kept inline to avoid circular deps.
const TYPE_HINTS: Record<string, Record<string, "string" | "number" | "boolean" | "uuid" | "date">> = {
  nursery: { id: "uuid", name: "string" },
  children: { id: "uuid", full_name: "string", nursery_id: "uuid", date_of_birth: "date" },
  classes: { id: "uuid", name: "string", nursery_id: "uuid", teacher_id: "uuid" },
  staff: { id: "uuid", full_name: "string", nursery_id: "uuid" },
  attendance: { id: "uuid", nursery_id: "uuid", child_id: "uuid", date: "date" },
  lessons: { id: "uuid", title: "string", nursery_id: "uuid" },
  invoices: { id: "uuid", nursery_id: "uuid", child_id: "uuid", amount: "number", paid: "boolean", due_date: "date" },
  expenses: { id: "uuid", nursery_id: "uuid", amount: "number", date: "date" },
  payroll_records: { id: "uuid", nursery_id: "uuid", staff_id: "uuid", amount: "number" },
  exams: { id: "uuid", title: "string", nursery_id: "uuid" },
  assignments: { id: "uuid", title: "string", nursery_id: "uuid", due_date: "date" },
};

// Common fields across many tables that look like dates / uuids by name
function inferType(field: string): "uuid" | "date" | "number" | "boolean" | null {
  if (field === "id" || field.endsWith("_id")) return "uuid";
  if (
    field === "date" ||
    field.endsWith("_at") ||
    field.endsWith("_date") ||
    field === "due_date" ||
    field === "date_of_birth"
  )
    return "date";
  if (
    field === "amount" ||
    field === "price" ||
    field === "total" ||
    field === "quantity" ||
    field.endsWith("_amount") ||
    field.endsWith("_count") ||
    field.endsWith("_price")
  )
    return "number";
  if (field.startsWith("is_") || field.startsWith("has_") || field === "active" || field === "paid")
    return "boolean";
  return null;
}

function tryFixUUID(v: any): { ok: boolean; value: any } {
  if (typeof v !== "string") return { ok: false, value: v };
  const cleaned = v.trim().toLowerCase();
  if (UUID_RE.test(cleaned)) return { ok: true, value: cleaned };
  return { ok: false, value: v };
}

function tryFixDate(v: any): { ok: boolean; value: any } {
  if (v === null || v === undefined || v === "") return { ok: false, value: v };
  if (typeof v === "number") {
    const d = new Date(v);
    return isNaN(d.getTime()) ? { ok: false, value: v } : { ok: true, value: d.toISOString() };
  }
  if (typeof v !== "string") return { ok: false, value: v };

  let s = v.trim();
  // Common formats: "YYYY/MM/DD", "DD/MM/YYYY", "DD-MM-YYYY"
  // Convert separators
  if (/^\d{4}\/\d{1,2}\/\d{1,2}/.test(s)) s = s.replace(/\//g, "-");
  if (/^\d{1,2}\/\d{1,2}\/\d{4}/.test(s)) {
    const [a, b, c] = s.split(/\s|T/)[0].split("/");
    s = `${c}-${b.padStart(2, "0")}-${a.padStart(2, "0")}`;
  } else if (/^\d{1,2}-\d{1,2}-\d{4}/.test(s)) {
    const [a, b, c] = s.split(/\s|T/)[0].split("-");
    s = `${c}-${b.padStart(2, "0")}-${a.padStart(2, "0")}`;
  }
  const d = new Date(s);
  if (isNaN(d.getTime())) return { ok: false, value: v };
  return { ok: true, value: d.toISOString() };
}

function tryFixNumber(v: any): { ok: boolean; value: any } {
  if (typeof v === "number" && !isNaN(v)) return { ok: true, value: v };
  if (typeof v !== "string") return { ok: false, value: v };
  const cleaned = v.trim().replace(/[, ]/g, "").replace(/[٠-٩]/g, (d) => String("٠١٢٣٤٥٦٧٨٩".indexOf(d)));
  if (cleaned === "") return { ok: false, value: v };
  const n = Number(cleaned);
  if (isNaN(n)) return { ok: false, value: v };
  return { ok: true, value: n };
}

function tryFixBoolean(v: any): { ok: boolean; value: any } {
  if (typeof v === "boolean") return { ok: true, value: v };
  if (v === 1 || v === "1") return { ok: true, value: true };
  if (v === 0 || v === "0") return { ok: true, value: false };
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    if (["true", "yes", "نعم", "y"].includes(s)) return { ok: true, value: true };
    if (["false", "no", "لا", "n"].includes(s)) return { ok: true, value: false };
  }
  return { ok: false, value: v };
}

function genUUID(): string {
  // RFC4122-ish v4 — good enough for restore (DB will accept it)
  if (typeof crypto !== "undefined" && (crypto as any).randomUUID) return (crypto as any).randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export interface AutoFixOptions {
  expectedNurseryId?: string;
  generateMissingIds?: boolean; // default true
}

export function autoFixBackup(
  data: Record<string, any[]>,
  options: AutoFixOptions = {}
): { fixedData: Record<string, any[]>; report: AutoFixReport } {
  const { expectedNurseryId, generateMissingIds = true } = options;
  const issues: AutoFixIssue[] = [];
  const byTable: Record<string, { changes: number; dropped: number }> = {};
  let totalChanges = 0;
  let droppedRows = 0;
  let fixedRows = 0;
  const fixedData: Record<string, any[]> = {};

  const record = (issue: AutoFixIssue) => {
    totalChanges++;
    const t = (byTable[issue.table] ||= { changes: 0, dropped: 0 });
    t.changes++;
    if (issues.length < SAMPLE_LIMIT) issues.push(issue);
  };

  for (const [table, rows] of Object.entries(data)) {
    if (!Array.isArray(rows)) {
      fixedData[table] = rows as any;
      continue;
    }
    byTable[table] ||= { changes: 0, dropped: 0 };

    const seenIds = new Set<string>();
    const out: any[] = [];

    rows.forEach((raw, idx) => {
      if (!raw || typeof raw !== "object") {
        droppedRows++;
        byTable[table].dropped++;
        return;
      }
      const row: Record<string, any> = { ...raw };
      let rowChanged = false;

      const hints = TYPE_HINTS[table] || {};

      for (const key of Object.keys(row)) {
        const val = row[key];
        const explicit = hints[key];
        const inferred = explicit || inferType(key);

        // 1) Trim strings
        if (typeof val === "string") {
          const trimmed = val.trim();
          if (trimmed !== val) {
            record({ table, rowIndex: idx, field: key, before: val, after: trimmed, reason: "تم إزالة المسافات الزائدة" });
            row[key] = trimmed;
            rowChanged = true;
          }
        }

        // 2) Empty string -> null (except required keys handled below)
        if (row[key] === "") {
          record({ table, rowIndex: idx, field: key, before: "", after: null, reason: "حقل فارغ تم تحويله إلى null" });
          row[key] = null;
          rowChanged = true;
          continue;
        }

        if (row[key] === null || row[key] === undefined) continue;

        // 3) Type coercions based on inferred type
        if (inferred === "uuid") {
          const fix = tryFixUUID(row[key]);
          if (fix.ok && fix.value !== row[key]) {
            record({ table, rowIndex: idx, field: key, before: row[key], after: fix.value, reason: "تطبيع تنسيق UUID" });
            row[key] = fix.value;
            rowChanged = true;
          } else if (!fix.ok && key !== "id") {
            // Drop malformed FK fields (set to null) — DB will reject otherwise
            record({ table, rowIndex: idx, field: key, before: row[key], after: null, reason: "UUID غير صالح — تم تفريغ الحقل" });
            row[key] = null;
            rowChanged = true;
          }
        } else if (inferred === "date") {
          const fix = tryFixDate(row[key]);
          if (fix.ok && fix.value !== row[key]) {
            record({ table, rowIndex: idx, field: key, before: row[key], after: fix.value, reason: "تحويل التاريخ إلى تنسيق ISO" });
            row[key] = fix.value;
            rowChanged = true;
          } else if (!fix.ok) {
            record({ table, rowIndex: idx, field: key, before: row[key], after: null, reason: "تاريخ غير صالح — تم تفريغ الحقل" });
            row[key] = null;
            rowChanged = true;
          }
        } else if (inferred === "number") {
          const fix = tryFixNumber(row[key]);
          if (fix.ok && fix.value !== row[key]) {
            record({ table, rowIndex: idx, field: key, before: row[key], after: fix.value, reason: "تحويل النص إلى رقم" });
            row[key] = fix.value;
            rowChanged = true;
          }
        } else if (inferred === "boolean") {
          const fix = tryFixBoolean(row[key]);
          if (fix.ok && fix.value !== row[key]) {
            record({ table, rowIndex: idx, field: key, before: row[key], after: fix.value, reason: "تحويل القيمة إلى boolean" });
            row[key] = fix.value;
            rowChanged = true;
          }
        }
      }

      // 4) Force / fill nursery_id for tenant scope
      if (expectedNurseryId && "nursery_id" in row && (!row.nursery_id || row.nursery_id === "")) {
        record({ table, rowIndex: idx, field: "nursery_id", before: row.nursery_id, after: expectedNurseryId, reason: "تعيين nursery_id الصحيح" });
        row.nursery_id = expectedNurseryId;
        rowChanged = true;
      }

      // 5) Generate missing id
      if (generateMissingIds && (!row.id || row.id === "")) {
        const newId = genUUID();
        record({ table, rowIndex: idx, field: "id", before: row.id, after: newId, reason: "تم توليد معرّف UUID جديد" });
        row.id = newId;
        rowChanged = true;
      }

      // 6) Drop duplicates
      if (row.id) {
        if (seenIds.has(row.id)) {
          droppedRows++;
          byTable[table].dropped++;
          record({ table, rowIndex: idx, field: "id", before: row.id, after: null, reason: "سجل مكرر تم حذفه" });
          return;
        }
        seenIds.add(row.id);
      }

      if (rowChanged) fixedRows++;
      out.push(row);
    });

    fixedData[table] = out;
  }

  return {
    fixedData,
    report: {
      totalChanges,
      fixedRows,
      droppedRows,
      byTable,
      issues,
      fullCount: totalChanges,
    },
  };
}
